import prepareSpokenText from './prepareSpokenText';

/**
 * Browser voice conversation engine shared by AI Search and PropStream
 * Intelligence (ATAI-231 / ATAI-323).
 *
 * Owns everything that talks to a browser voice API: SpeechRecognition,
 * speechSynthesis, the getUserMedia + AudioContext waveform, the silence and
 * restart timers, mic/sound mute and End. It has no React import so it can be
 * driven from the search header (a class component) and, through
 * useVoiceConversation, from function components.
 *
 * It only knows about voice. Each screen keeps its own rules — what "send"
 * means, word limits, layout, when a reply should be spoken — and wires them
 * in through the callbacks below.
 *
 * Callbacks:
 *   onStateChange(partial) — the voice state keys that changed (names match the
 *     search header's state keys, so its render code reads them unchanged).
 *   onSubmit(text)          — a finished transcript the screen should send.
 *   onTranscript(text)      — every non-empty live transcript while listening.
 *
 * Options:
 *   stripMarkup — strip markdown and emojis before speaking. Off for AI Search,
 *     whose replies are plain text and whose spoken output must not change.
 */

const VOICE_RECOGNITION_ERROR_MESSAGES = {
  'not-allowed': 'Microphone permission is blocked. Please allow microphone access and try again.',
  'permission-denied':
    'Microphone permission is blocked. Please allow microphone access and try again.',
  'service-not-allowed':
    'Microphone access requires a secure browser connection. Please use HTTPS and try again.',
  'no-speech': "I didn't catch that. Please try speaking again.",
  network: 'Voice search had a network issue. Please try again.',
  'audio-capture':
    'Voice search could not access your microphone. Please check your microphone and try again.',
};

export const getVoiceRecognitionErrorMessage = (error) =>
  VOICE_RECOGNITION_ERROR_MESSAGES[error] || 'Voice search could not start. Please try again.';

export const VOICE_UNSUPPORTED_BROWSER_TOOLTIP =
  'For the best voice chat experience, use Google Chrome or Microsoft Edge.';

const isBraveBrowser = () => typeof navigator !== 'undefined' && Boolean(navigator.brave);

const isFirefoxBrowser = () =>
  typeof navigator !== 'undefined' && /firefox|fxios/i.test(navigator.userAgent || '');

export const isUnsupportedVoiceBrowser = () => isBraveBrowser() || isFirefoxBrowser();

// The two SpeechRecognition error codes that mean the browser refused the
// microphone. These surface as the MicPermissionModal instead of inline text,
// because the user has to leave the page and change a browser setting to
// recover. Every other code stays inline. `not-allowed` is the spec's code;
// `permission-denied` is emitted by older WebKit builds.
const MIC_PERMISSION_ERRORS = ['not-allowed', 'permission-denied'];

const isMicPermissionError = (error) => MIC_PERMISSION_ERRORS.includes(error);

// Speech-recognizer `network` gets a popup too (ATAI-308): the controller
// raises voiceNetworkError and each screen shows its own popup for it, titled
// with VOICE_NETWORK_ERROR_TITLE. `no-speech` and other codes stay inline.
const isVoiceNetworkError = (error) => error === 'network';

export const VOICE_NETWORK_ERROR_TITLE = getVoiceRecognitionErrorMessage('network');

export const VOICE_WAVEFORM_BAR_COUNT = 48;
export const VOICE_WAVEFORM_MIN_HEIGHT = 4;
export const VOICE_WAVEFORM_MAX_HEIGHT = 18;
const VOICE_WAVEFORM_NOISE_FLOOR = 0.012;
const VOICE_WAVEFORM_SENSITIVITY = 11.5;
const VOICE_WAVEFORM_BAR_HEIGHTS = [8, 11, 14, 10, 16, 12, 9, 15, 11, 16, 13, 10, 14, 9, 12, 8];
const VOICE_RESTART_DELAY_MS = 250;
const MAX_CONSECUTIVE_NO_SPEECH_RESTARTS = 3;

export const getInitialVoiceWaveformSamples = () =>
  Array.from(
    { length: VOICE_WAVEFORM_BAR_COUNT },
    (v, idx) => VOICE_WAVEFORM_BAR_HEIGHTS[idx % VOICE_WAVEFORM_BAR_HEIGHTS.length]
  );

export const clampVoiceAmplitude = (value) => Math.max(0, Math.min(1, value));

export const INITIAL_VOICE_STATE = {
  voiceError: null,
  voiceRecognitionSupported: false,
  voiceOutputSupported: false,
  voiceListening: false,
  voiceModeActive: false,
  voiceSpeaking: false,
  voiceTranscript: '',
  micMuted: false,
  voiceOutputMuted: false,
  voiceReplyPlaying: false,
  voiceAudioReactive: false,
  voiceWaveformSamples: null,
  micPermissionBlocked: false,
  voiceNetworkError: false,
};

const noop = () => {};

export default class VoiceConversationController {
  constructor({ onStateChange, onSubmit, onTranscript, stripMarkup = false } = {}) {
    this.onStateChange = onStateChange || noop;
    this.onSubmit = onSubmit || noop;
    this.onTranscript = onTranscript || noop;
    this.stripMarkup = stripMarkup;

    this.state = { ...INITIAL_VOICE_STATE };
    // The screen's "a reply is loading" flag. Listening never (re)starts while
    // a response is in flight.
    this.busy = false;
    this.destroyed = false;

    this.recognition = null;
    this.voiceSilenceTimer = null;
    this.voiceRestartTimer = null;
    this.voiceShouldSubmitOnEnd = false;
    this.voiceKeepModeAfterStop = false;
    this.finalVoiceTranscript = '';
    this.latestVoiceTranscript = '';
    this.consecutiveNoSpeechCount = 0;
    this.voiceUtterance = null;
    this.voiceAudioStream = null;
    this.voiceAudioContext = null;
    this.voiceAudioSource = null;
    this.voiceAudioAnalyser = null;
    this.voiceAudioData = null;
    this.voiceAudioFrame = null;
    this.voiceAudioRequestId = 0;
    this.voiceSmoothedAmplitude = 0;
    this.voiceWaveformSamples = getInitialVoiceWaveformSamples();
    this.voiceWaveformUpdateTime = 0;

    this.restartIfReady = this.restartIfReady.bind(this);
  }

  getState() {
    return this.state;
  }

  setState(partial) {
    if (this.destroyed) return;
    this.state = { ...this.state, ...partial };
    this.onStateChange(partial);
  }

  setBusy(busy) {
    this.busy = Boolean(busy);
  }

  getSpeechRecognitionConstructor() {
    if (typeof window === 'undefined') return null;
    return window.SpeechRecognition || window.webkitSpeechRecognition || null;
  }

  detectSupport() {
    const SpeechRecognition = this.getSpeechRecognitionConstructor();
    this.setState({
      voiceRecognitionSupported: Boolean(SpeechRecognition) && !isUnsupportedVoiceBrowser(),
      voiceOutputSupported:
        typeof window !== 'undefined' &&
        Boolean(window.speechSynthesis && window.SpeechSynthesisUtterance),
    });
  }

  clearVoiceSilenceTimer() {
    if (this.voiceSilenceTimer) {
      clearTimeout(this.voiceSilenceTimer);
      this.voiceSilenceTimer = null;
    }
  }

  clearVoiceRestartTimer() {
    if (this.voiceRestartTimer) {
      clearTimeout(this.voiceRestartTimer);
      this.voiceRestartTimer = null;
    }
  }

  scheduleVoiceRecognitionRestart() {
    this.clearVoiceRestartTimer();
    this.voiceRestartTimer = setTimeout(() => {
      this.voiceRestartTimer = null;
      this.restartIfReady();
    }, VOICE_RESTART_DELAY_MS);
  }

  cleanupVoiceAudioResources({ stream, source, analyser, audioContext }) {
    try {
      if (source && source.disconnect) source.disconnect();
    } catch (_e) {
      // Voice visualization cleanup should never block recognition cleanup.
    }

    try {
      if (analyser && analyser.disconnect) analyser.disconnect();
    } catch (_e) {
      // Some browsers throw if a node was never connected.
    }

    if (stream && stream.getTracks) {
      stream.getTracks().forEach((track) => track.stop());
    }

    try {
      if (audioContext && audioContext.state !== 'closed' && audioContext.close) {
        const closePromise = audioContext.close();
        if (closePromise && closePromise.catch) closePromise.catch(() => {});
      }
    } catch (_e) {
      // Ignore close errors; the stream tracks have already been stopped.
    }
  }

  stopVoiceAudioAnalyzer(resetSamples = true) {
    this.voiceAudioRequestId += 1;

    if (this.voiceAudioFrame && typeof window !== 'undefined' && window.cancelAnimationFrame) {
      window.cancelAnimationFrame(this.voiceAudioFrame);
    }

    this.voiceAudioFrame = null;
    this.cleanupVoiceAudioResources({
      stream: this.voiceAudioStream,
      source: this.voiceAudioSource,
      analyser: this.voiceAudioAnalyser,
      audioContext: this.voiceAudioContext,
    });
    this.voiceAudioStream = null;
    this.voiceAudioContext = null;
    this.voiceAudioSource = null;
    this.voiceAudioAnalyser = null;
    this.voiceAudioData = null;
    this.voiceSmoothedAmplitude = 0;
    this.voiceWaveformUpdateTime = 0;

    if (resetSamples) this.voiceWaveformSamples = getInitialVoiceWaveformSamples();

    this.setState({
      voiceAudioReactive: false,
      voiceWaveformSamples: resetSamples ? null : this.voiceWaveformSamples,
    });
  }

  isVoiceAudioRequestCurrent(requestId) {
    return (
      this.voiceAudioRequestId === requestId &&
      !this.destroyed &&
      this.state.voiceListening &&
      !this.state.micMuted
    );
  }

  async startVoiceAudioAnalyzer() {
    if (
      typeof window === 'undefined' ||
      !navigator?.mediaDevices?.getUserMedia ||
      !window.requestAnimationFrame
    ) {
      return false;
    }

    const AudioContextConstructor = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextConstructor) return false;

    this.stopVoiceAudioAnalyzer(false);
    const requestId = this.voiceAudioRequestId + 1;
    this.voiceAudioRequestId = requestId;

    let stream = null;
    let audioContext = null;
    let source = null;
    let analyser = null;

    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });

      if (!this.isVoiceAudioRequestCurrent(requestId)) {
        this.cleanupVoiceAudioResources({ stream, source, analyser, audioContext });
        return false;
      }

      audioContext = new AudioContextConstructor();
      if (audioContext.state === 'suspended' && audioContext.resume) {
        await audioContext.resume();
      }

      if (!this.isVoiceAudioRequestCurrent(requestId)) {
        this.cleanupVoiceAudioResources({ stream, source, analyser, audioContext });
        return false;
      }

      analyser = audioContext.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.76;

      source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      this.voiceAudioStream = stream;
      this.voiceAudioContext = audioContext;
      this.voiceAudioSource = source;
      this.voiceAudioAnalyser = analyser;
      this.voiceAudioData = new Uint8Array(analyser.fftSize);
      this.voiceSmoothedAmplitude = 0;
      this.voiceWaveformSamples = Array.from(
        { length: VOICE_WAVEFORM_BAR_COUNT },
        () => VOICE_WAVEFORM_MIN_HEIGHT
      );
      this.voiceWaveformUpdateTime = 0;

      this.setState({
        voiceAudioReactive: true,
        voiceWaveformSamples: this.voiceWaveformSamples,
      });

      this.voiceAudioFrame = window.requestAnimationFrame((timestamp) =>
        this.updateVoiceWaveform(timestamp)
      );
      return true;
    } catch (_e) {
      this.cleanupVoiceAudioResources({ stream, source, analyser, audioContext });
      if (this.voiceAudioRequestId === requestId) {
        this.setState({
          voiceAudioReactive: false,
          voiceWaveformSamples: null,
        });
      }
      return false;
    }
  }

  updateVoiceWaveform(timestamp) {
    if (!this.voiceAudioAnalyser || !this.voiceAudioData || typeof window === 'undefined') return;

    if (
      timestamp &&
      this.voiceWaveformUpdateTime &&
      timestamp - this.voiceWaveformUpdateTime < 50
    ) {
      this.voiceAudioFrame = window.requestAnimationFrame((nextTimestamp) =>
        this.updateVoiceWaveform(nextTimestamp)
      );
      return;
    }

    this.voiceWaveformUpdateTime = timestamp || Date.now();
    this.voiceAudioAnalyser.getByteTimeDomainData(this.voiceAudioData);

    let sumSquares = 0;
    for (let i = 0; i < this.voiceAudioData.length; i += 1) {
      const centered = (this.voiceAudioData[i] - 128) / 128;
      sumSquares += centered * centered;
    }

    const rms = Math.sqrt(sumSquares / this.voiceAudioData.length);
    const normalizedRms = Math.max(0, rms - VOICE_WAVEFORM_NOISE_FLOOR);
    const amplitude = clampVoiceAmplitude(normalizedRms * VOICE_WAVEFORM_SENSITIVITY);
    const smoothingWeight = amplitude > this.voiceSmoothedAmplitude ? 0.55 : 0.28;
    this.voiceSmoothedAmplitude =
      this.voiceSmoothedAmplitude * (1 - smoothingWeight) + amplitude * smoothingWeight;

    const level = clampVoiceAmplitude(Math.pow(this.voiceSmoothedAmplitude, 0.52));
    const height = Math.round(
      VOICE_WAVEFORM_MIN_HEIGHT + (VOICE_WAVEFORM_MAX_HEIGHT - VOICE_WAVEFORM_MIN_HEIGHT) * level
    );

    this.voiceWaveformSamples = this.voiceWaveformSamples.slice(1).concat(height);
    this.setState({
      voiceAudioReactive: true,
      voiceWaveformSamples: this.voiceWaveformSamples,
    });

    this.voiceAudioFrame = window.requestAnimationFrame((nextTimestamp) =>
      this.updateVoiceWaveform(nextTimestamp)
    );
  }

  markVoiceSpeechStart() {
    this.clearVoiceSilenceTimer();
    this.setState({ voiceSpeaking: true });
  }

  scheduleVoiceSpeechSettled() {
    this.clearVoiceSilenceTimer();
    this.voiceSilenceTimer = setTimeout(() => {
      this.voiceSilenceTimer = null;
      this.setState({ voiceSpeaking: false });
    }, 650);
  }

  finishVoiceRecognition(shouldSubmit) {
    const text = (
      this.latestVoiceTranscript ||
      this.state.voiceTranscript ||
      this.finalVoiceTranscript ||
      ''
    ).trim();
    const keepVoiceModeActive = this.voiceKeepModeAfterStop;
    this.voiceKeepModeAfterStop = false;
    this.clearVoiceSilenceTimer();
    this.stopVoiceAudioAnalyzer();
    this.recognition = null;
    this.voiceShouldSubmitOnEnd = false;
    this.finalVoiceTranscript = '';
    this.latestVoiceTranscript = '';
    this.setState({
      voiceListening: false,
      voiceModeActive: keepVoiceModeActive || (shouldSubmit && Boolean(text)),
      voiceSpeaking: false,
      voiceTranscript: shouldSubmit && text ? text : '',
    });

    if (this.destroyed) return;
    if (shouldSubmit && text && !this.state.micMuted) {
      this.consecutiveNoSpeechCount = 0;
      this.onSubmit(text);
    } else if (keepVoiceModeActive && !shouldSubmit) {
      this.scheduleVoiceRecognitionRestart();
    }
  }

  stopListening(shouldSubmit, keepVoiceModeActive = false) {
    if (!keepVoiceModeActive) this.clearVoiceRestartTimer();
    this.voiceShouldSubmitOnEnd = shouldSubmit;
    this.voiceKeepModeAfterStop = keepVoiceModeActive;
    this.stopVoiceAudioAnalyzer();
    if (!this.recognition) {
      this.finishVoiceRecognition(false);
      return;
    }

    try {
      if (shouldSubmit) this.recognition.stop();
      else this.recognition.abort();
    } catch (e) {
      this.finishVoiceRecognition(false);
    }
  }

  restartIfReady() {
    if (
      this.state.voiceModeActive &&
      !this.state.voiceListening &&
      !this.state.micMuted &&
      !this.busy &&
      !this.state.voiceReplyPlaying &&
      !this.voiceUtterance
    ) {
      this.start();
      return true;
    }

    return false;
  }

  start() {
    if (
      !this.state.voiceRecognitionSupported ||
      this.state.micMuted ||
      this.busy ||
      this.state.voiceReplyPlaying ||
      this.voiceUtterance
    ) {
      // no engine, mic off by the user, or the assistant is still talking
      return;
    }

    const SpeechRecognition = this.getSpeechRecognitionConstructor();
    if (!SpeechRecognition || isUnsupportedVoiceBrowser()) {
      this.setState({
        voiceRecognitionSupported: false,
        voiceError: null,
      });
      return;
    }

    if (!this.state.voiceModeActive) this.consecutiveNoSpeechCount = 0;
    this.cancelSpeech();
    this.stopListening(false);
    this.setState({
      voiceModeActive: true,
      voiceListening: true,
      voiceSpeaking: false,
      voiceTranscript: '',
      voiceError: null,
      micPermissionBlocked: false,
      voiceNetworkError: false,
    });

    const recognition = new SpeechRecognition();
    this.recognition = recognition;
    this.voiceShouldSubmitOnEnd = true;
    this.finalVoiceTranscript = '';
    this.latestVoiceTranscript = '';

    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    const isCurrentRecognition = () => this.recognition === recognition;

    recognition.onstart = () => {
      if (!isCurrentRecognition()) return;
      this.setState({
        voiceModeActive: true,
        voiceListening: true,
        voiceSpeaking: false,
        voiceTranscript: '',
        voiceError: null,
      });
      if (!this.destroyed) this.startVoiceAudioAnalyzer();
    };

    recognition.onspeechstart = () => {
      if (isCurrentRecognition()) this.markVoiceSpeechStart();
    };
    recognition.onsoundstart = recognition.onspeechstart;

    recognition.onspeechend = () => {
      if (isCurrentRecognition()) this.scheduleVoiceSpeechSettled();
    };
    recognition.onsoundend = recognition.onspeechend;

    recognition.onresult = (event) => {
      if (!isCurrentRecognition()) return;
      this.consecutiveNoSpeechCount = 0;

      let finalTranscript = '';
      let interimTranscript = '';

      for (let i = 0; i < event.results.length; i++) {
        const result = event.results[i];
        const transcript = result[0]?.transcript || '';
        if (result.isFinal) finalTranscript += ` ${transcript}`;
        else interimTranscript += ` ${transcript}`;
      }

      const transcript = `${finalTranscript} ${interimTranscript}`.trim();
      if (finalTranscript.trim()) this.finalVoiceTranscript = finalTranscript.trim();
      if (transcript) this.latestVoiceTranscript = transcript;

      if (transcript) {
        this.markVoiceSpeechStart();
        this.scheduleVoiceSpeechSettled();
        this.setState({
          voiceTranscript: transcript,
          voiceError: null,
        });
        if (!this.destroyed) this.onTranscript(transcript);
      }
    };

    recognition.onerror = (event) => {
      if (!isCurrentRecognition()) return;

      if (event?.error === 'aborted') {
        this.stopVoiceAudioAnalyzer();
        return;
      }

      this.voiceShouldSubmitOnEnd = false;
      this.clearVoiceSilenceTimer();
      this.stopVoiceAudioAnalyzer();

      if (event?.error === 'no-speech' && this.state.voiceModeActive) {
        this.consecutiveNoSpeechCount += 1;
        const shouldRestartAfterNoSpeech =
          this.consecutiveNoSpeechCount <= MAX_CONSECUTIVE_NO_SPEECH_RESTARTS;
        this.voiceKeepModeAfterStop = shouldRestartAfterNoSpeech;

        if (!shouldRestartAfterNoSpeech) {
          this.setState({
            voiceListening: false,
            voiceModeActive: false,
            voiceSpeaking: false,
            voiceError: getVoiceRecognitionErrorMessage(event?.error),
          });
        }
        return;
      }

      const stoppedState = {
        voiceListening: false,
        voiceModeActive: false,
        voiceSpeaking: false,
      };

      if (isMicPermissionError(event?.error)) {
        // microphone permission denied by the browser: a dead mic is a
        // dead-end for voice, so it gets the modal rather than the inline
        // error line, which is easy to miss
        this.setState({
          ...stoppedState,
          voiceError: null,
          micPermissionBlocked: true,
          voiceNetworkError: false,
        });
      } else if (isVoiceNetworkError(event?.error)) {
        // speech-service network failure: same forced-choice popup as
        // blocked-mic (Dismiss / Try Again), raised by the screen
        this.setState({
          ...stoppedState,
          voiceError: null,
          voiceNetworkError: true,
        });
      } else {
        // every other recognition failure (no speech heard, capture device,
        // speech service): recoverable, stays as inline text
        this.setState({
          ...stoppedState,
          voiceError: getVoiceRecognitionErrorMessage(event?.error),
          voiceNetworkError: false,
        });
      }
    };

    recognition.onend = () => {
      if (!isCurrentRecognition()) return;
      this.finishVoiceRecognition(this.voiceShouldSubmitOnEnd);
    };

    try {
      recognition.start();
    } catch (e) {
      this.finishVoiceRecognition(false);

      if (e?.name === 'NotAllowedError') {
        // start() rejected outright because microphone permission is denied
        this.setState({ voiceError: null, micPermissionBlocked: true });
      } else if (typeof window !== 'undefined' && window.isSecureContext === false) {
        // page is not on HTTPS, so the speech service refuses to run
        this.setState({ voiceError: getVoiceRecognitionErrorMessage('service-not-allowed') });
      } else {
        // start() failed for some other reason, generic inline message
        this.setState({ voiceError: getVoiceRecognitionErrorMessage() });
      }
    }
  }

  // The mic button: stop and send what was heard, or start listening.
  toggleListening() {
    if (this.state.voiceListening) {
      this.stopListening(true);
      return;
    }
    this.start();
  }

  retryMicPermission() {
    // Close first: start() re-opens the modal itself if the browser still
    // refuses the microphone.
    this.setState({ micPermissionBlocked: false });
    this.start();
  }

  dismissMicPermission() {
    this.setState({ micPermissionBlocked: false });
  }

  retryNetworkError() {
    // Close first: start() raises the popup again if the network still fails.
    this.setState({ voiceNetworkError: false });
    this.start();
  }

  dismissNetworkError() {
    this.setState({ voiceNetworkError: false });
  }

  // Leaves voice mode without sending (screen closed, mode switched, cleared).
  // Mutes are kept; only End resets them.
  reset() {
    this.stopListening(false);
    this.cancelSpeech();
    this.setState({
      voiceError: null,
      voiceNetworkError: false,
      voiceTranscript: '',
      voiceModeActive: false,
    });
  }

  clearTranscript() {
    this.setState({ voiceTranscript: '' });
  }

  // The End button: stop listening and speaking, restore the text box, and
  // clear both mutes. Nothing is sent.
  end() {
    this.clearVoiceRestartTimer();
    this.voiceShouldSubmitOnEnd = false;
    this.voiceKeepModeAfterStop = false;
    this.finalVoiceTranscript = '';
    this.latestVoiceTranscript = '';

    const recognition = this.recognition;
    this.recognition = null;
    if (recognition) {
      try {
        recognition.abort();
      } catch (_e) {
        // End should restore the UI even if the browser already stopped recognition.
      }
    }

    this.clearVoiceSilenceTimer();
    this.stopVoiceAudioAnalyzer();
    this.cancelSpeech();
    this.setState({
      voiceModeActive: false,
      voiceListening: false,
      voiceSpeaking: false,
      voiceTranscript: '',
      voiceReplyPlaying: false,
      voiceAudioReactive: false,
      voiceWaveformSamples: null,
      micMuted: false,
      voiceOutputMuted: false,
      voiceError: null,
      voiceNetworkError: false,
    });
  }

  toggleMicMute() {
    this.setState({ micMuted: !this.state.micMuted });
    if (this.state.micMuted) {
      this.stopListening(false, true);
    } else if (this.state.voiceModeActive && !this.state.voiceListening) {
      this.restartIfReady();
    }
  }

  toggleOutputMute() {
    this.setState({ voiceOutputMuted: !this.state.voiceOutputMuted });
    if (this.state.voiceOutputMuted) {
      // cancelSpeech nulls this.voiceUtterance synchronously, so speak()'s
      // finish() bails out on its stale-utterance check and never resumes
      // listening. Skip its own state update and restart recognition
      // explicitly, mirroring what finish() would have done on a natural
      // speech end.
      this.cancelSpeech(false);
      this.setState({ voiceReplyPlaying: false });
      this.restartIfReady();
    }
  }

  cancelSpeech(updateState = true) {
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    this.voiceUtterance = null;
    if (updateState && this.state.voiceReplyPlaying) {
      this.setState({ voiceReplyPlaying: false });
    }
  }

  // Reads a reply aloud, then listens again once it finishes.
  speak(text) {
    if (
      !text ||
      typeof window === 'undefined' ||
      !window.speechSynthesis ||
      !window.SpeechSynthesisUtterance ||
      this.state.voiceOutputMuted ||
      this.destroyed
    ) {
      return;
    }

    this.cancelSpeech(false);
    const utterance = new window.SpeechSynthesisUtterance(
      prepareSpokenText(text, { stripMarkup: this.stripMarkup })
    );
    this.voiceUtterance = utterance;

    const finish = () => {
      if (this.voiceUtterance !== utterance) return;
      this.voiceUtterance = null;
      this.setState({ voiceReplyPlaying: false });
      if (!this.destroyed) this.restartIfReady();
    };

    utterance.onstart = () => {
      if (this.voiceUtterance !== utterance) return;
      this.setState({ voiceReplyPlaying: true });
    };
    utterance.onend = finish;
    utterance.onerror = finish;

    window.speechSynthesis.speak(utterance);
  }

  destroy() {
    this.destroyed = true;
    this.clearVoiceRestartTimer();
    this.stopListening(false);
    this.cancelSpeech(false);
  }
}
